<template>
  <div class="sub border marg-20-bottom">
    <div class="sub__user-header space-between flex align-center">
      <div class="flex align-center">
        <img class="marg-20-right rounded sub__user-img"
             :src="user.default_thumb_url" alt=""/>
        <h2>{{ user.name }}</h2>
        <DDropdown>
          <ul slot="list" style="width: 180px">
            <li><a :href="'/calendars/' + user.user_calendar_id" class="marg-5-left text-link"
                   target="_blank">{{ i18n[locale]['see_calendar'] }}</a></li>
            <li @click="setActiveAbsentUser(user)">{{ i18n[locale].add_sub_to_multiple_lessons }}</li>
          </ul>
        </DDropdown>
      </div>
      <div class="sub__user-header__right">
        <div class="absence-status">
          <label class="d-label marg-10-bottom">{{ i18n[locale]['absent_handled'] }}</label>
          <div class="absence__status" @click="setSubStatusDropdown(12)">
            <div class="dot" :class="schedulerUserHandledCls(user)">
            </div>
            <div class="dropdown absence__status__dropdown sub-status__dropdown"
                 :class="{'sub-status__dropdown--loading': isSubmittingAbsenceStatus}"
                 v-if="isSubStatusDropdown(12)"
                 v-on-clickaway="closeSubStatusDropdown">
              <div class="do-spinner__container" v-if="isSubmittingAbsenceStatus">
                <div class="do-spinner do-spinner--large"></div>
              </div>
              <ul>
                <li @click.stop="setAbsenceStatus(0)">
                  <div class="dot dot-white"></div>
                  {{ $t('vue2.sub_scheduler_popover.not_handled') }}
                </li>
                <li @click.stop="setAbsenceStatus(1)">
                  <div class="dot"></div>
                  {{ $t('vue2.sub_scheduler_popover.handled') }}
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="absence-reasons">
          <div class="absence-reasons__absence" v-for="reason in absenceReasons">
            <p class="absence-reasons__absence__note">
              <label class="d-label">{{ i18n[locale]['note'] }}:</label>
              <weip :lesson="reason" noteType="teacher_notes" @close="updateTeacherNote"></weip>
            </p>
            <div>
              <div class="absence-reasons__absence__title"><p><b>{{ reason.title }}</b></p>
                <p v-if="reason.event_serial_id">
                  <template v-if="getIsAbsenceOngoing(reason)">
                    <span v-if="getIsAbsenceAutoGenerated(reason)"
                          class="badge badge--orange-warning badge--small badge--clickable"
                          @click="setTheEditAbsenceReason(reason)">{{ i18n[locale]['auto_created'] }}</span>
                    <span v-else class="badge badge--orange badge--small badge--clickable"
                          @click="setTheEditAbsenceReason(reason)">{{ i18n[locale]['ongoing_absence'] }}</span>
                  </template>
                  <span v-if="!getIsAbsenceOngoing(reason) && getIsAbsenceMultipleDays(reason)"
                        class="badge badge--small">{{ i18n[locale]['multiple_days'] }}</span>
                </p>
              </div>
              <p>
                <template v-if="getSerialListItem(reason)">
                  {{ formatDate(getSerialListItem(reason).from_date) }}
                  - {{ formatDate(getSerialListItem(reason).to_date) }}, {{ showTime(reason.start) }} -
                  {{ showTime(reason.end) }}
                </template>
                <template v-else>
                  {{ absenceDate(reason.start) }} -
                  <span>{{ showTime(reason.end) }}</span>
                </template>
              </p>
            </div>
            <div class="absence-reasons__absence__right">
              <div class="absence-reasons__absence__right__edits">
                <span class="icon-bin icon__bg-circle deleteAbsence"
                      @click="setReadyToDelete(reason)"
                      :content="$t('vue2.common.delete')"
                      v-tippy="{
                    arrow: true,
                    placement : 'top'
                  }">
                </span>
                <span class="icon-wrench icon__bg-circle"
                      v-if="getSerialListItem(reason)"
                      @click="setTheEditAbsenceReason(reason)"
                      :content="$t('vue2.common.edit')"
                      v-tippy="{
                    arrow: true,
                    placement : 'top'
                  }">
                </span>
              </div>
              <div class="activity-log icon__bg-circle"
                   @click="showLog(reason)"
                   :content="i18n[locale]['log']"
                   v-tippy="{
                    arrow: true,
                    placement : 'left'
                  }">
                <span class="icon-clock"></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="sub-body">
      <h4>{{ i18n[locale]['lessons_on_day'] }}: {{ sortedEvents.length }}
        <br>
        {{ i18n[locale]['lessons_affected_by_absence'] }}: {{ overlappingArr.length }}
      </h4>
      <table class="sub-table" v-if="sortedEvents.length > 0">
        <tr>
          <th>{{ $filters.capitalize(showDate(sortedEvents[0].start)) }}</th>
          <th>{{ i18n[locale]['subject'] }}</th>
          <th>{{ i18n[locale]['class'] }}</th>
          <th>{{ i18n[locale]['same_time'] }}</th>
          <th>{{ i18n[locale]['substitute'] }}</th>
          <th>{{ i18n[locale]['substitute_notes'] }}</th>
          <th>{{ i18n[locale]['status'] }}</th>
        </tr>
        <tr class="sub-lesson"
            v-for="(lesson, lessonIdx) in sortedEvents"
            :data-uuid="user.id + '_' + lesson.id"
            :key="user.id + '_' + lesson.id"
            :class="[{'lesson-overlaps': lesson.v_isOverlapping}, {'sub-lesson__absence-overlap--handled': lesson.v_status && lesson.v_status !== 2}, {'sub-lesson__absence-overlap--pending': lesson.v_status === 2}]">
          <td class="sub-lesson__time" :aria-i18n="i18n[locale]['absent']">
            <div class="sub-lesson__time__content">
              <div>{{ $filters.LT(lesson.start) }} - {{ $filters.LT(lesson.end) }}</div>
              <button class="bttn bttn-sub-small hint--right"
                      :aria-label="i18n[locale]['split_lesson']"
                      @click="setActiveSplitLesson(lesson)"><span class="icon-wrench"></span></button>
            </div>
          </td>
          <td class="sub-lesson__title">
            {{ lesson.title }}
          </td>
          <td style="width: 100px">
            <a v-for="cal in lesson.klassesAndOther"
               :key="cal.id"
               :href="cal.url">
              {{ cal.name }}
            </a>
          </td>
          <td class="sub-lesson__others">
            <p v-if="lesson.v_others.length > 0"><b>{{ i18n[locale].on_the_lesson }}</b></p>
            <div class="sub-lesson__others__user" v-for="(user, othersIdx) in lesson.v_others"
                 :key="user.id"
                 :class="{'sub-lesson__others__user--absent': user.v_isUserAbsentFromLesson.length > 0}"
                 :content="user.v_isUserAbsentFromLesson"
                 v-tippy="{
                    arrow: false,
                    zIndex: 123123123123,
                    placement : 'top-start',
                    onShow: () => user.v_isUserAbsentFromLesson.length > 0
                  }"
                 v-if="othersIdx < 3 || lesson.showAllOtherOnLesson">
              <span class="icon-warning" v-if="user.v_isUserAbsentFromLesson.length > 0"></span> <span class="icon-user"
                                                                                                       v-else></span>
              {{ user.name }}
            </div>
            <span class="text-blue pointer" @click="setShowOtherOnLesson(lesson.id)"
                  v-if="lesson.v_others.length > 3 && !lesson.showAllOtherOnLesson">{{
                i18n[locale].see_all
              }}</span>
            <span class="text-blue pointer" @click="setShowOtherOnLesson(null)"
                  v-if="lesson.showAllOtherOnLesson">{{ i18n[locale].see_less }}</span>
            <div class="other_in_klass" v-if="lesson.otherLessonsInKlassThatOverlap.length > 0">
              <p><b>{{ i18n[locale].same_time_in_class }}</b></p>
              <div v-for="(otherLesson, otherKey) in lesson.otherLessonsInKlassThatOverlap" class="lesson"
                   :style="{'borderLeft': '2px solid '+ otherLesson.color +''}"
                   :key="otherLesson.id"
                   v-if="otherKey < 2 || lesson.showOtherLessonsInKlassThatOverlap">
                {{ otherLesson.title }}, {{ $filters.LT(otherLesson.start) }} - {{ $filters.LT(otherLesson.end) }}
                <div v-for="(user, key) in otherLesson.users"
                     :key="user.id"
                     v-if="key < 2 || otherLesson.showAllUsers"
                     :class="{'other-lesson-user--absent': user.v_isUserAbsent}"
                     :content="user.v_absenceInfo"
                     v-tippy="{
                       arrow: false,
                       placement: 'top',
                       onShow: () => user.v_isUserAbsent
                     }">
                  <span class="icon-warning" v-if="user.v_isUserAbsent"></span>
                  <span class="icon-user" v-else></span>
                  {{ user.name }}
                </div>
                <span class="text-blue pointer" @click="setOtherLessonShowAllUsers(otherLesson.id)"
                      v-if="otherLesson.users.length > 2 && !otherLesson.showAllUsers">{{ i18n[locale].see_all }}</span>
                <span class="text-blue pointer" @click="setOtherLessonShowAllUsers(null)"
                      v-if="otherLesson.showAllUsers">{{ i18n[locale].see_less }}</span>
              </div>
            </div>
            <span class="text-blue pointer" @click="setShowOtherLessonsInKlassThatOverlap(lesson.id)"
                  v-if="lesson.otherLessonsInKlassThatOverlap.length > 2 && !lesson.showOtherLessonsInKlassThatOverlap">{{
                i18n[locale].view_all_lessons
              }}</span>
            <span class="text-blue pointer" @click="setShowOtherLessonsInKlassThatOverlap(null)"
                  v-if="lesson.showOtherLessonsInKlassThatOverlap">{{ i18n[locale].view_less_lessons }}</span>

          </td>
          <td style="width: 40%"
              :class="{'d-select-lookalike': !openDSelect(lesson) && lesson.v_subs.length === 0}"
              @click="setDSelect(lesson, lessonIdx)"
              :aria-placeholder-label="i18n[locale]['search']">
            <dSelectSub
              v-if="openDSelect(lesson)"
              :show="openDSelect(lesson)"
              :items="usersForDSelect"
              :startTime="lesson.start"
              :endTime="lesson.end"
              :user="user"
              :eventId="lesson.id"
              :itemsSelected="lesson.v_subs"
              :othersOnLesson="lesson.v_others"
              :isUpdatingUsers="isUpdatingPotentialSubs"
              @add="addSubstitute($event, lesson)"
              @remove="removeSubstitute($event, lesson)">
            </dSelectSub>
            <div class="d-select__input-look"
                 v-if="!openDSelect(lesson) && lesson.v_subs.length > 0">
              <div v-for="sub in lesson.v_subs"
                   :key="sub.id"
                   class="d-select__selected__item"
                   :class="{'d-select__seleted__item--is-error-user': sub.isErrorUser}">
                {{ sub.name }}
                <div class="item__icon-warning marg-5-left hint--error hint--top"
                     :aria-label="textFromSubLessonsThatOverlap(lesson, sub.dayEvents)"
                     v-if="subLessonsThatOverlap(lesson, sub.dayAllEvents).length > 0"></div>
                <div type="button" v-if="!sub.isErrorUser"
                     @click.stop="removeSubstitute(sub, lesson)"
                     class="d-select__selected__item__remove">×
                </div>
              </div>
            </div>
          </td>
          <td class="sub_teacher_notes" style="width: 250px;"
              :class="[
              {'icon-pencil eip-lookalike eip-lookalike--empty': !isActiveEIP(lessonIdx) && lesson.teacher_notes.length < 1},
              {'eip-lookalike eip-filled': !isActiveEIP(lessonIdx) && lesson.teacher_notes.length > 0}]"
              @click="setActiveEIP(lessonIdx)">
            <div v-if="!isActiveEIP(lessonIdx)" v-html="lesson.teacher_notes"></div>
            <weip v-if="isActiveEIP(lessonIdx)"
                  noteType="teacher_notes"
                  :lesson="lesson"
                  :isOpen="true"
                  @close="updateTeacherNote">
            </weip>
          </td>
          <td style="width: 20px">
            <div v-if="lesson.v_substituted && !isSubStatusPendingActive" class="flex set-status"
                 :content="$t('vue2.sub_scheduler.is_substituted')"
                 v-tippy="{
                placement: 'left',
                arrow: false,
                animateFill: false,
                animation : 'shift-away',
                zIndex: '999999992',
                boundary: 'window'
            }">
              <div class="dot"></div>
            </div>
            <div class="flex set-status pointer" v-else
                 @click="setSubStatusDropdown(lesson.id)">
              <div class="dot"
                   :class="[statusDotClass(lesson), {'dot--loading': lesson.v_isLoading }]">
              </div>
              <div class="arrow icon-arrow-down"></div>
              <div class="dropdown sub-status__dropdown" v-if="isSubStatusDropdown(lesson.id)"
                   v-on-clickaway="closeSubStatusDropdown">
                <ul>
                  <li @click.stop="setSubStatus(lesson, 0)" v-if="!lesson.v_substituted">
                    <div class="dot dot-white"></div>
                    {{ $t('vue2.sub_scheduler_popover.not_handled') }}
                  </li>
                  <li @click.stop="setSubStatus(lesson, 2)" v-if="isSubStatusPendingActive">
                    <div class="dot dot-yellow"></div>
                    {{ $t('vue2.sub_scheduler_popover.pending') }}
                  </li>
                  <li @click.stop="setSubStatus(lesson, 1)">
                    <div class="dot"></div>
                    {{ $t('vue2.sub_scheduler_popover.handled') }}
                  </li>
                </ul>
              </div>
            </div>
          </td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script>
import AbsenceReason from './SubsUserAbsenceReason'
import DSelectSub from '../DSelect/DSelectSubsSub'
import weip from '../common/AppEIPWYZIWYG'
import {mixin as clickaway} from 'vue-clickaway'
import docendo from '../../docendo'
import {maskHiddenAbsenceTitlesInAvailability} from '../../utils/hiddenAbsenceTitle'
import Moment from 'moment'
import {extendMoment} from 'moment-range'
import DDropdown from '../common/DDropdown'

const moment = extendMoment(Moment)
const I18N = {
  da: {
    subject: 'Fag',
    class: 'Klasse',
    other_users: 'I samme tidsrum',
    substitute: 'Vikar',
    substitute_notes: 'Vikarnoter',
    todays_lessons: 'Dagens lektioner',
    see_all: 'Se alle',
    see_less: 'Se færre',
    search: 'Søg på person',
    multiple_days: 'Flere dage',
    lessons_on_day: 'Lektioner på dagen',
    lessons_affected_by_absence: 'Lektioner påvirket af fravær',
    absent: 'Fraværende',
    reason: 'Årsag',
    absent_period: 'Fraværsperiode',
    note: 'Note',
    split_lesson: 'Opdel lektion',
    same_time: 'I samme tidsrum',
    on_the_lesson: 'Underviser',
    same_time_in_class: 'Samtidig i klassen',
    view_all: 'Se alle',
    view_less: 'Se færre',
    view_all_lessons: 'Se alle lektioner',
    view_less_lessons: 'Se færre lektioner',
    log: 'Historik',
    user_not_found: 'Brugeren er fjernet fra skolen',
    absent_handled: 'Håndteret',
    ongoing_absence: 'Løbende fravær',
    auto_created: 'Oprettet Automatisk',
    see_calendar: 'Se kalender',
    add_sub_to_multiple_lessons: 'Vikardæk flere lektioner'
  },
  en: {
    subject: 'Subject',
    class: 'Class',
    other_users: 'Other users',
    substitute: 'Substitute',
    substitute_notes: 'Substitute notes',
    todays_lessons: 'Todays lessons',
    see_all: 'View all',
    see_less: 'View less',
    search: 'Search',
    multiple_days: 'Multiple days',
    lessons_on_day: 'Lessons this day',
    lessons_affected_by_absence: 'Lessons affected by absence',
    absent: 'Absent',
    reason: 'Reason',
    absent_period: 'Period of absence',
    note: 'Note',
    note_for_absent: 'Note for absent',
    split_lesson: 'Split lesson in two',
    same_time: 'At the same time',
    on_the_lesson: 'Teachers',
    same_time_in_class: 'In the same class',
    view_all: 'View all',
    view_less: 'View less',
    view_all_lessons: 'View all lessons',
    view_less_lessons: 'View fewer lessons',
    log: 'Activity log',
    user_not_found: 'User is removed from school',
    absent_handled: 'Covered',
    ongoing_absence: 'Ongoing absence',
    auto_created: 'Autogenerated',
    see_calendar: 'See calendar',
    add_sub_to_multiple_lessons: 'Add substitute to multiple lessons'
  },
  nb: {
    subject: 'Fag',
    class: 'Klasse',
    other_users: 'Også tilknyttet',
    substitute: 'Vikar',
    substitute_notes: 'Vikarnotater',
    todays_lessons: 'Dagens timer',
    see_all: 'Se alle',
    see_less: 'Se mindre',
    search: 'Søk etter person',
    multiple_days: 'Flere dager',
    lessons_on_day: 'Undervisningstimer denne dag',
    lessons_affected_by_absence: 'Timer påvirket av fravær',
    absent: 'Fraværende',
    reason: 'Årsak',
    absent_period: 'Fraværsperiode',
    note: 'Notat',
    note_for_absent: 'Eventuell notat til fraværet',
    split_lesson: 'Del opp timen',
    same_time: 'I samme tidsrom',
    on_the_lesson: 'Undervisere',
    same_time_in_class: 'Samtidig i klassen',
    view_all: 'Se alle',
    view_less: 'Se færre',
    view_all_lessons: 'Se alle timer',
    view_less_lessons: 'Se færre timer',
    log: 'Historikk',
    user_not_found: 'Brukeren er fjernet fra skolen',
    absent_handled: 'Håndtert',
    ongoing_absence: 'Løpende lengre fravær',
    auto_created: 'Opprettet automatisk',
    see_calendar: 'Se kalender',
    add_sub_to_multiple_lessons: 'Legg til vikar for flere leksjoner'
  }
}

export default {
  mixins: [clickaway],
  components: {DDropdown, DSelectSub, weip, AbsenceReason},
  props: {
    user: {},
    activeDSelect: {
      type: String,
      default: null
    },
    allEvents: {
      type: Array,
      default: () => []
    },
    allEventsInAHash: {
      type: Object,
      default: () => {
      }
    },
    allUsersAndEvents: {
      type: Object,
      default: () => {
      }
    },
    allCalendarsInAHash: {
      type: Object,
      default: () => {
      }
    },
    allUsersSortedOnName: {
      type: Array,
      default: () => []
    },
    longAbsentListInHash: {
      type: Object,
      default: () => {
      }
    },
    date: {
      type: [Object, String],
      default: () => moment()
    },
    calsAndEventIds: {
      type: Object,
      default: () => {
      }
    },
    absentUsersHandledHash: {
      type: Object,
      default: () => {
      }
    },
    isSubStatusPendingActive: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isSubmittingAbsenceStatus: false,
      activeEIP: [],
      showOtherOnLesson: -1,
      showDSub: -1,
      searchText: '',
      occupiedUsers: [],
      usersForDSelect: [],
      subStatusDropdown: null,
      isUpdatingPotentialSubs: false,
      theActiveShowAllOtherOnLessonId: null,
      theActiveShowOtherLessonsInKlassThatOverlap: null,
      theActiveOtherLessonShowAllUsers: null,
      theLoadingLessons: []
    }
  },
  watch: {
    date () {
      this.resetAllFormData()
    }
  },
  computed: {
    i18n () {
      return I18N
    },
    locale () {
      return this.$store.getters.locale
    },
    userLessons () {
      return this.user.dayEvents.map(eventId => this.allEventsInAHash[eventId])
    },
    countryIsDK () {
      return window.SCHOOL.country === 'DK'
    },
    filterAbsentUser () {
      return this.allUsersSortedOnName.filter(user => user.id !== this.user.id)
    },
    sortedEvents () {
      // Pre-compute absence ranges once
      const absenceRanges = this.absenceReasons.map(absence => ({
        id: absence.id,
        range: moment.range(absence.start, absence.end)
      }))

      return this.userLessons
        .map(lesson => {
          const lessonRange = moment.range(lesson.start, lesson.end)

          // Check overlap with absence (previously had side effects)
          const isOverlapping = absenceRanges.some(absence => lessonRange.intersect(absence.range))

          // Pre-compute klassesAndOther
          const klassesAndOther = []
          const calEvents = lesson.calendar_events
          for (let i = 0; i < calEvents.length; i++) {
            const cal = calEvents[i]
            if (cal && (cal.calendar_type === 'klass' || cal.calendar_type === 'other')) {
              klassesAndOther.push({
                ...cal,
                url: window.location.origin + '/calendars/' + cal.calendar_id
              })
            }
          }

          // Pre-compute othersOnLesson
          const others = []
          for (let i = 0; i < calEvents.length; i++) {
            const calEvent = calEvents[i]
            if (this.allUsersAndEvents[calEvent.calendar_id] && !calEvent.substitute && calEvent.calendar_id !== this.user.user_calendar_id) {
              const user = this.allUsersAndEvents[calEvent.calendar_id]
              others.push({
                ...user,
                v_isUserAbsentFromLesson: this.doesAbsenceOverlapLesson(lesson.id, user.dayAbsence)
              })
            }
          }

          // Pre-compute subs
          const subs = []
          for (let i = 0; i < calEvents.length; i++) {
            const event = calEvents[i]
            if (event.substitute && event.substitute_for_user_id === this.user.id) {
              const subUser = this.allUsersAndEvents[event.calendar_id]
              subs.push(subUser || this.userNotFoundPlaceholder)
            }
          }

          return {
            ...lesson,
            v_substituted: this.getIsUserSubstituted(lesson),
            v_status: this.getSubstitutionStatusForUserCalId(lesson),
            v_isLoading: this.theLoadingLessons.indexOf(lesson.id) > -1,
            v_isOverlapping: isOverlapping,
            v_others: others,
            v_subs: subs,
            showAllOtherOnLesson: lesson.id === this.theActiveShowAllOtherOnLessonId,
            klassesAndOther: klassesAndOther,
            otherLessonsInKlassThatOverlap: this.otherLessonsInKlassThatOverlap(lesson),
            showOtherLessonsInKlassThatOverlap: lesson.id === this.theActiveShowOtherLessonsInKlassThatOverlap
          }
        })
        .sort((a, b) => moment(a.start).unix() - moment(b.start).unix())
    },
    overlappingArr () {
      return this.sortedEvents.filter(lesson => lesson.v_isOverlapping).map(lesson => lesson.id)
    },
    absenceReasons () {
      return this.user.dayAbsence.map(eventId => this.allEventsInAHash[eventId])
    },
    userNotFoundPlaceholder () {
      return {
        id: 0,
        name: this.i18n[this.locale]['user_not_found'],
        type: 'user',
        url: '/calendar',
        calendar_id: 0,
        dayAbsence: [],
        dayEvents: [],
        dayAllEvents: [],
        isErrorUser: true,
        default_thumb_url: '',
        user_tags: [],
        occupied: false,
        isOnLesson: false,
        currentEventId: 0,
        user_calendar_id: 0
      }
    }
  },
  methods: {
    showDate (date) {
      return moment(date).format('dddd, D. MMM')
    },
    showTime (date) {
      return moment(date).format('LT')
    },
    formatDate (date) {
      return moment(date).format('ll')
    },
    absenceDate (date) {
      return moment(date).format('DD. MMM, [kl]. LT')
    },
    schedulerUserHandledCls (item) {
      const userStatus = this.getIsUserAbsentHandled(item)
      if (userStatus === 0) {
        return 'dot-white'
      }
      if (userStatus === 2) {
        return 'dot-yellow'
      }
    },
    statusDotClass (lesson) {
      if (lesson.v_status === 2) {
        return 'dot-yellow'
      }
      if (lesson.v_status === 0) {
        return 'dot-white'
      }
    },
    setOtherLessonShowAllUsers (lessonId) {
      this.theActiveOtherLessonShowAllUsers = lessonId
    },
    setShowOtherLessonsInKlassThatOverlap (lessonId) {
      this.theActiveShowOtherLessonsInKlassThatOverlap = lessonId
    },
    getIsAbsenceMultipleDays (reason) {
      let serialItem = this.getSerialListItem(reason)
      return serialItem && moment(serialItem.from_date).format('YYYY-MM-DD') !== moment(serialItem.to_date).format('YYYY-MM-DD')
    },
    getIsAbsenceAutoGenerated (reason) {
      let statusObj = reason.substitution_status ? JSON.parse(reason.substitution_status) : {}
      return statusObj['extended_at'] && moment(statusObj['extended_at']).isSame(moment(this.date).format('YYYY-MM-DD'))
    },
    setActiveAbsentUser () {
      this.$store.commit('setActiveAbsentUser', this.user)
    },
    setReadyToDelete (reason) {
      this.$store.commit('setIsActiveAbsenceReasonIsReadyToDelete', true)
      this.$store.commit('setActiveAbsenceReason', reason)
    },
    showLog (event) {
      this.$emit('showLog', event)
    },
    getSubs (lesson) {
      let subCalIds = lesson.calendar_events.filter(event => {
        return event.substitute && event.substitute_for_user_id === this.user.id
      })
      return subCalIds.map(sub => {
        return this.allUsersAndEvents[sub.calendar_id] ? this.allUsersAndEvents[sub.calendar_id] : this.userNotFoundPlaceholder
      })
    },
    setSubStatusDropdown (lessonId) {
      this.subStatusDropdown = lessonId
    },
    isSubStatusDropdown (lessonId) {
      return this.subStatusDropdown === lessonId
    },
    getIsUserSubstituted (lesson) {
      const isUserSubstituted = lesson.calendar_events.some(calEvent => calEvent.substitute_for_user_id === this.user.id)
      if (isUserSubstituted) {
        return true
      }
    },
    getSubstitutionStatusForUserCalId (lesson) {
      const statusObj = lesson.substitution_status ? JSON.parse(lesson.substitution_status) : {}
      const userStatus = statusObj[this.user.user_calendar_id] ? statusObj[this.user.user_calendar_id].status : 0
      if (this.isSubStatusPendingActive) {
        return userStatus
      } else {
        const isUserSubstituted = lesson.calendar_events.some(calEvent => calEvent.substitute_for_user_id === this.user.id)
        if (isUserSubstituted || userStatus === 1 || lesson && lesson.canceled === true) {
          return 1
        } else {
          return 0
        }
      }
    },
    setSubStatus (lesson, num) {
      this.pushLoadingLesson(lesson.id)
      const statusData = {...this.substitution_status}
      if (num === 0) {
        delete statusData[this.user.user_calendar_id]
      } else {
        statusData[this.user.user_calendar_id] = {status: num}
      }
      docendo.updateEvent(lesson.school_id, lesson.id, {substitution_status: JSON.stringify(statusData)}, success => {
        this.$store.commit('updateSubStatusOnEvent', {
          'eventId': lesson.id,
          'status': JSON.stringify(statusData)
        })
        this.spliceLoadingLesson(lesson.id)
      })
      this.closeSubStatusDropdown()
    },
    getIsUserAbsentHandled () {
      return this.absentUsersHandledHash[this.user.id] ? this.absentUsersHandledHash[this.user.id] : 0
    },
    setAbsenceStatus (num) {
      this.isSubmittingAbsenceStatus = true
      if (this.user.dayEvents.length < 1) {
        this.user.dayAbsence.forEach(lessonId => {
          const lesson = this.allEventsInAHash[lessonId]
          let statusData = lesson.substitution_status ? JSON.parse(lesson.substitution_status) : {}
          statusData[this.user.user_calendar_id] = {'status': num}
          docendo.updateEvent(lesson.school_id, lesson.id, {substitution_status: JSON.stringify(statusData)}, success => {
            this.$store.commit('updateSubStatusOnEvent', {
              'eventId': lesson.id,
              'status': JSON.stringify(statusData)
            })
          })
        })
      } else {
        const groupBySchoolId = (lessons) => {
          return lessons.reduce((groups, lesson) => {
            const schoolId = lesson.school_id
            if (!groups[schoolId]) {
              groups[schoolId] = []
            }
            groups[schoolId].push(lesson)
            return groups
          }, {})
        }

        // Collect lessons to update
        const lessonsToUpdate = this.user.dayEvents
          .filter(lessonId => {
            const lesson = this.allEventsInAHash[lessonId]
            return !this.isSubStatusPendingActive ? !this.getIsUserSubstituted(lesson) : true
          })
          .map(lessonId => {
            const lesson = this.allEventsInAHash[lessonId]
            let statusData = lesson.substitution_status ? JSON.parse(lesson.substitution_status) : {}
            statusData[this.user.user_calendar_id] = {status: num}

            return {
              ...lesson,
              substitution_status: JSON.stringify(statusData)
            }
          })

        // Group lessons by school_id
        const groupedLessons = groupBySchoolId(lessonsToUpdate)

        // Loop through each school_id group and make API calls
        Object.entries(groupedLessons).forEach(([schoolId, lessons]) => {
          const eventsToUpdate = lessons.map(lesson => ({
            event_id: lesson.id,
            substitution_status: lesson.substitution_status
          }))

          docendo.updateEvents(schoolId, eventsToUpdate, success => {
            if (success) {
              // Commit updates to Vuex store
              eventsToUpdate.forEach(event => {
                this.$store.commit('updateSubStatusOnEvent', {
                  eventId: event.event_id,
                  status: event.substitution_status
                })
              })
            } else {
              console.error(`Failed to update events for school_id: ${schoolId}`)
            }
            setTimeout(_ => {
              this.isSubmittingAbsenceStatus = false
              this.closeSubStatusDropdown()
            }, 0)
          })
        })
      }
    },
    closeSubStatusDropdown () {
      this.subStatusDropdown = null
    },
    isActiveEIP (idx) {
      return this.activeEIP.indexOf(idx) > -1
    },
    setActiveEIP (idx) {
      if (this.activeEIP.indexOf(idx) < 0) {
        this.activeEIP.push(idx)
      }
    },
    isShowOtherOnLesson (idx) {
      return this.showOtherOnLesson === idx
    },
    setShowOtherOnLesson (lessonId) {
      this.theActiveShowAllOtherOnLessonId = lessonId
      // this.sortedEvents[idx].showAllOtherOnLesson = Boolean
    },
    subLessonsThatOverlap (lesson, lessonsArr) {
      let filterCurrLesson = lessonsArr.filter(eventid => eventid !== lesson.id)
      let arr = filterCurrLesson.map(eventId => this.allEventsInAHash[eventId])
      let arrWithOutPresence = arr.filter(event => event.event_type !== null)
      return arrWithOutPresence.filter(dayLesson => {
        const range1 = moment.range(dayLesson.start, dayLesson.end)
        const range2 = moment.range(lesson.start, lesson.end)
        return range1.intersect(range2)
      })
    },
    textFromSubLessonsThatOverlap (lesson, lessonsArr) {
      let filterCurrLesson = lessonsArr.filter(eventid => eventid !== lesson.id)
      let arr = filterCurrLesson.map(eventId => this.allEventsInAHash[eventId])
      let arrWithOutPResence = arr.filter(event => event.event_type !== null)
      let lessonsArrToTitles = arrWithOutPResence.filter(dayLesson => {
        const range1 = moment.range(dayLesson.start, dayLesson.end)
        const range2 = moment.range(lesson.start, lesson.end)
        return range1.intersect(range2)
      })
      return lessonsArrToTitles.map(lesson => lesson.title)
    },
    otherLessonsInKlassThatOverlap (lesson) {
      // Find alle klasser eventet er i
      const cals = this.calsOnLesson(lesson)
      const klassCals = cals.filter(cal => cal && cal.type === 'klass')
      const klassCalEventsIDs = klassCals.map(cal => this.calsAndEventIds[cal.id])
      const allEventsInKlass = klassCalEventsIDs.map(obj => obj.lessons)
      const lessonsInKlass = allEventsInKlass[0]
        ? allEventsInKlass[0].map(id => this.allEventsInAHash[id])
        : []

      const lessonRange = moment.range(lesson.start, lesson.end)

      // Find alle events i klassen der overlapper
      return lessonsInKlass
        .filter(event => {
          const range2 = moment.range(event.start, event.end)
          return event.id !== lesson.id && lessonRange.overlaps(range2)
        })
        .map(event => {
          const usersOnEvent = event.calendar_events
            .filter(cal => this.allUsersAndEvents[cal.calendar_id])
            .map(cal => {
              const user = this.allUsersAndEvents[cal.calendar_id]
              const absenceOverlap = this.doesAbsenceOverlapLesson(lesson.id, user.dayAbsence || [])
              return {
                ...user,
                v_isUserAbsent: absenceOverlap.length > 0,
                v_absenceInfo: absenceOverlap.join(', ')
              }
            })

          // Return a new event object with the added properties
          return {
            ...event,
            showAllUsers: event.id === this.theActiveOtherLessonShowAllUsers,
            users: usersOnEvent
          }
        })
    },
    setUsersForDSelect () {
      this.filterAbsentUser.map(obj => {
        let occ = false
        let isOnLesson = true
        let user = {
          id: obj.id,
          default_thumb_url: obj.default_thumb_url,
          name: obj.name,
          user_tags: obj.user_tags,
          occupied: occ,
          isOnLesson: isOnLesson,
          currentEventId: 1,
          user_calendar_id: obj.user_calendar_id
        }
        this.usersForDSelect.push(user)
      })
      this.usersForDSelect.push(this.userNotFoundPlaceholder)
    },
    updateUsersForList (lesson) {
      this.usersForDSelect.forEach((obj, idx) => {
        this.usersForDSelect[idx].currentEventId = lesson.id
        this.usersForDSelect[idx].isOnLesson = this.othersOnLesson(lesson).filter(user => user.id === obj.id).length > 0
        if (this.occupiedUsers[obj.user_calendar_id]) {
          this.usersForDSelect[idx].occupied = this.occupiedUsers[obj.user_calendar_id]
        } else {
          this.usersForDSelect[idx].occupied = false
        }
        this.isUpdatingPotentialSubs = false
      })
    },
    getOccupiedUsers (lesson) {
      this.isUpdatingPotentialSubs = true
      docendo.schoolAvailabilities(lesson.school_id, 'user', lesson.start, lesson.end, data => {
        let toArr = maskHiddenAbsenceTitlesInAvailability(JSON.parse(data), this.$store, lesson.school_id)
        let newUsers = {}
        for (let i = 0; i < toArr.length; i++) {
          newUsers[toArr[i].calendar_id] = toArr[i]
        }
        this.occupiedUsers = newUsers
        this.updateUsersForList(lesson)
      })
    },
    setDSelect (lesson) {
      this.$emit('setActiveDSelect', this.user.id + '_' + lesson.id)
      this.getOccupiedUsers(lesson)
    },
    openDSelect (lesson) {
      return this.activeDSelect === this.user.id + '_' + lesson.id
    },
    calIdsOnLesson (lesson) {
      let calIds = lesson.calendar_events.map(cal => cal.calendar_id)
      return calIds.filter(calIds => calIds !== this.user.user_calendar_id)
    },
    calsOnLesson (lesson) {
      return this.calIdsOnLesson(lesson).filter(calId => this.allCalendarsInAHash[calId]).map(calId => this.allCalendarsInAHash[calId])
    },
    othersOnLesson (lesson) {
      let users = []
      for (let i = 0; i < lesson.calendar_events.length; i++) {
        // Remove substitute and absentUser
        if (this.allUsersAndEvents[lesson.calendar_events[i].calendar_id] && !lesson.calendar_events[i].substitute && lesson.calendar_events[i].calendar_id !== this.user.user_calendar_id) {
          const user = this.allUsersAndEvents[lesson.calendar_events[i].calendar_id]
          user['v_isUserAbsentFromLesson'] = this.doesAbsenceOverlapLesson(lesson.id, user.dayAbsence)
          users.push(user)
        }
      }
      return users
    },
    doesAbsenceOverlapLesson (lessonId, absenceIds) {
      let absenceOverlapping = []
      const absenceEvents = absenceIds.map(eventId => this.allEventsInAHash[eventId])
      const lessonEvent = this.allEventsInAHash[lessonId]
      absenceEvents.forEach(absenceEvent => {
        const range1 = moment.range(absenceEvent.start, absenceEvent.end)
        const range2 = moment.range(lessonEvent.start, lessonEvent.end)
        if (range1.intersect(range2)) {
          const absentLessonReadable = absenceEvent.title + ' ' + moment(absenceEvent.start).format('LT') + '-' + moment(absenceEvent.end).format('LT')
          absenceOverlapping.push(absentLessonReadable)
        }
      })
      return absenceOverlapping
    },
    pushLoadingLesson (lessonId) {
      const idx = this.theLoadingLessons.indexOf(lessonId)
      if (idx < 0) {
        this.theLoadingLessons.push(lessonId)
      }
    },
    spliceLoadingLesson (lessonId) {
      const idx = this.theLoadingLessons.indexOf(lessonId)
      if (idx > -1) {
        this.theLoadingLessons.splice(idx, 1)
      }
    },
    addSubstitute (userObj, lesson) {
      this.pushLoadingLesson(lesson.id)
      if (this.isSubStatusPendingActive) {
        this.setSubStatus(lesson, 2)
      }
      docendo.eventAddSubstitute(lesson.id, userObj.id, this.user.id, data => {
        this.spliceLoadingLesson(lesson.id)
        this.$store.commit('updateEventOnAllEventsInAHash', JSON.parse(data))
        this.$store.commit('addEventOnAllUsersWithEvents', {cal_id: userObj.user_calendar_id, event_id: lesson.id})
      })
    },
    removeSubstitute (userObj, lesson) {
      this.pushLoadingLesson(lesson.id)
      // Check if any subs for the sub that needs to be removed
      let subsForTheSub = []
      lesson.calendar_events.forEach(calEvent => {
        if (calEvent.substitute && calEvent.substitute_for_user_id === userObj.id) {
          subsForTheSub.indexOf(calEvent.calendar_id) < 0 ? subsForTheSub.push(calEvent.calendar_id) : null
        }
      })
      let userAndSubsForUser = {
        user: this.user,
        lesson: lesson,
        subToBeRemoved: userObj,
        subsForTheSubToBeRemoved: subsForTheSub
      }
      if (subsForTheSub.length > 0) {
        this.$emit('removeSubs', userAndSubsForUser)
        this.resetAllFormData()
        this.spliceLoadingLesson(lesson.id)
        return
      }
      this.setSubStatus(lesson, 0)
      docendo.eventRemoveSubstitute(lesson.id, userObj.id, this.user.id, data => {
        this.spliceLoadingLesson(lesson.id)
        this.$store.commit('updateEventOnAllEventsInAHash', JSON.parse(data))
        this.$store.commit('removeEventOnAllUsersWithEvents', {
          cal_id: userObj.user_calendar_id,
          event_id: lesson.id
        })
      })
    },
    resetAllFormData () {
      this.$emit('setActiveDSelect', null)
      this.activeEIP = []
      this.showOtherOnLesson = -1
      this.showDSub = -1
    },
    getSerialListItem (reason) {
      return this.longAbsentListInHash[reason.event_serial_id] || false
    },
    getIsAbsenceOngoing (reason) {
      return this.getSerialListItem(reason) && this.getSerialListItem(reason).ongoing
    },
    selectedUser () {
      this.$emit('selectedUser')
    },
    setActiveSplitLesson (lesson) {
      this.$emit('splitLesson', lesson)
    },
    updateTeacherNote (obj) {
      docendo.updateEvent(obj.lesson.school_id, obj.lesson.id, {[obj.noteType]: obj.text}, success => {
        console.log('opdateret')
      })
    },
    setTheEditAbsenceReason (reason) {
      let absence = this.getSerialListItem(reason) ? this.getSerialListItem(reason) : reason
      this.$store.commit('setActiveAbsenceReason', absence)
    }
  },
  mounted () {
    this.setUsersForDSelect()
  }
}
</script>

<style scoped>
@import '../../assets/css/variables.css';

.sub {
  border-radius: 4px;
  background: #fff;
}

.sub__user-img {
  width: 50px;
  height: 50px;
}

.sub__user-header {
  padding: 5px 10px;
  border-radius: 3px 3px 0 0;
  background-color: #fff;
  border-bottom: 1px solid #d8d8d8;
}

.sub-body {
  padding: 10px;
}

.sub-table {
  border-collapse: collapse;
  width: 100%;
}

.sub-table th {
  background-color: #f6f7f8;
  border: 1px solid #e0e0e0;
  text-align: center;
  font-size: 12px;
  padding: 5px;
  line-height: 1.2;
  font-weight: 700;
}

.sub-table td {
  vertical-align: middle;
  text-align: left;
  padding: 5px;
  line-height: 1.5;
  font-size: 10px;
  color: #3d4340;
}

.sub-lesson {
  margin-bottom: 2px;
  background-color: #f6f7f8;
  border-bottom: 1px solid #ccc;
}

.sub-lesson.lesson-overlaps {
  background: white;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.10), 0 3px 1px -2px rgba(0, 0, 0, 0.12), 0 1px 5px 0 rgba(0, 0, 0, 0.2);
}

.sub-lesson > td:first-child {
  position: relative;
  vertical-align: bottom;
  min-width: 100px;
}

.sub-lesson.lesson-overlaps > .sub-lesson__time {
  border-left: 2px solid #564437;
}

.sub-lesson.lesson-overlaps .sub-lesson__time:after {
  content: attr(aria-i18n);
  position: absolute;
  left: -1px;
  top: 4px;
  height: 8px;
  padding: 2px;
  background-color: rgb(86, 68, 55);
  color: white;
  text-transform: uppercase;
  font-size: 7px;
}

.sub-lesson__time__content {
  display: flex;
  min-height: 30px;
  min-width: 120px;
  align-items: flex-end;
  justify-content: space-between;
}

.sub-lesson.lesson-overlaps.sub-lesson__absence-overlap--handled > .sub-lesson__time {
  border-left: 2px solid #3ecf8e !important;
}

.sub-lesson.lesson-overlaps.sub-lesson__absence-overlap--handled > .sub-lesson__time:after {
  background-color: #57d49c7a;
  color: #228659;
}

.sub-lesson.lesson-overlaps.sub-lesson__absence-overlap--pending > .sub-lesson__time {
  border-left: 2px solid #f0b100 !important;
}

.sub-lesson.lesson-overlaps.sub-lesson__absence-overlap--pending > .sub-lesson__time:after {
  background-color: #f0b100;
  color: #725501;
}

.sub-lesson > div {
  margin-right: 5px;
}

.d-select-lookalike {
  height: 42px;
  position: relative;
}

.d-select-lookalike:after {
  content: attr(aria-placeholder-label);
  border-radius: 4px;
  display: flex;
  flex-wrap: wrap;
  transition: width 0.25s;
  padding-top: 7px;
  padding-bottom: 7px;
  padding-left: 9px;
  background: #ffffff;
  cursor: text;
  border: 1px solid #acb6be;
}


.d-select__selected__item {
  display: flex;
  position: relative;
  align-items: center;
  box-sizing: border-box;
  width: max-content;
  color: #333;
  background-color: #f0f0f0;
  border: 1px solid #ccc;
  border-radius: 4px;
  height: 26px;
  margin: 4px 1px 4px 3px;
  padding: 1px .25em;
  float: left;
  line-height: 24px;
}

.d-select__selected__item.d-select__seleted__item--is-error-user {
  color: #FA565A;
  font-style: italic;
}

.d-select__selected__item__remove {
  float: none;
  margin-right: 0;
  font-size: 20px;
  margin-left: 5px;
  appearance: none;
  padding: 0;
  cursor: pointer;
  background: 0 0;
  border: 0;
  font-weight: 700;
  color: #000;
  text-shadow: 0 1px 0 #fff;
  opacity: .2;
}

.d-select__selected__item__remove:hover {
  opacity: 1;
}

.d-select__header__search {
  width: 100%;
  transition: width 0.25s;
  border-radius: 4px;
}

.d-select__input-look {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  transition: width 0.25s;
  border-radius: 4px;
  background: white;
  cursor: text;
  border: 1px solid #acb6be;
}

.d-select__input-look.active {
  border: 1px solid var(--blue);
  border-radius: 4px 4px 0 0;
}

.warning-signs {
  display: flex;
  align-items: center;
}

warning-signs__sign {
  margin-right: 10px;
  display: flex;
}

.warning-sign > span {
  margin-right: 5px;
}

.item__icon-warning {
  display: block;
  padding: 2px 4px;
  height: 14px;
  width: 14px;
  font-size: 10px;
  background-color: rgba(251, 127, 152, 0.5);
  border-left: 2px solid #fb7f98;
  color: black;
}

.d-select__selected__item__secret-label {
  display: none !important;
  position: absolute;
  width: auto;
  z-index: 100;
  align-items: center;
  bottom: calc(100%);
  left: calc(100% - 39px);
  font-size: 10px;
  padding: 0 6px 0 4px;
  box-shadow: 4px 4px 8px rgba(0, 0, 0, .3);
  background-color: #fdc4cf;
  border-left: 2px solid #fb7f98;
  color: black;
  white-space: nowrap;
}

.item__icon-warning:hover + .d-select__selected__item__secret-label {
  display: flex !important;
  transform: translateY(2px);
  transition: all 1s;
}

.eip-lookalike {
  position: relative;
  line-height: initial;
}

.eip-lookalike:before {
  position: absolute;
  top: 13px;
  left: 5px;
  z-index: 1;
  margin-left: 5px;
}

.eip-lookalike > div {
  padding: 10px;
  line-height: 1.5;
  font-size: 10px;
  color: #3d4340;
}

.eip-lookalike > div:hover {
  background-color: lightgoldenrodyellow;
}

.absence-reasons__absence {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  color: #333;
  border: 1px solid #d8d8d8;
  border-radius: 4px;
  padding: 2px 6px;
  margin-bottom: 5px;
  margin-left: 20px;
}

.absence-reasons__absence > div:first-child {
  display: flex;
  flex-direction: column;
  text-align: left;
}

.absence-reasons__absence__note {
  margin-right: 10px;
  align-items: start;
  flex-direction: column;
  display: flex;
  border-right: 1px solid #d8d8d8;
  padding-right: 10px;
  max-width: 650px;
}

.absence-reasons__absence__right__edits {
  display: flex;
  align-items: center;
}

.deleteAbsence > span {
  cursor: pointer;
  background-color: white;
  border-radius: 50%;
  color: #8e8e93;
  font-size: 14px;
  padding: 5px;
}

.deleteAbsence > span:hover {
  transition: all 0.25s;
  background-color: var(--danger);
  color: white;
}

h4.go_to_calendar {
  text-align: left;
}

.red-pill {
  border: 1px solid var(--orange);
  padding: 4px 8px;
  font-weight: bold;
  font-size: 10px;
  font-style: italic;
  border-radius: 4px;
}

.set-status {
  width: 38px;
  align-items: center;
  position: relative;
}

/* Basis styling til dot */
.dot {
  position: relative;
  width: 15px;
  height: 15px;
  background-color: #3ecf8e;
  border-radius: 50%;
  margin-right: 5px;
  border: 1px solid #3ecf8e;
}

/* Loader-effekt */
.dot.dot--loading:before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  z-index: 2;
  background: transparent;
  width: calc(100% + 4px);
  height: calc(100% + 4px);
  border-radius: 50%;
  border: 2px solid #d0d0d0; /* Light grey */
  border-top: 2px solid var(--purple); /* Blue */
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

/* Animation af spinner */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.dot-white {
  background-color: #ffffff;
  border-color: var(--dark-blue);
}

.dropdown {
  position: absolute;
  top: 0;
  right: 0;
  width: 144px;
  z-index: 100;
  background-color: #ffffff;
  font-size: 14px;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, .14), 0 1px 5px 0 rgba(0, 0, 0, .12), 0 3px 1px -2px rgba(0, 0, 0, .2);
  border-radius: 4px;
}

.dropdown li {
  display: flex;
  clear: both;
  color: rgba(0, 0, 0, .87);
  cursor: pointer;
  min-height: 50px;
  width: 100%;
  text-align: left;
  text-transform: none;
  font-size: 13px;
  font-weight: 700;
  line-height: 22px;
  padding: 14px 16px;
}

.dropdown li:hover {
  background-color: #f6f7f8;
}

.bttn-sub-small {
  height: 22px;
  width: 22px;
  overflow: initial;
  padding: 0;
  display: inline-block !important;
}

.lesson {
  border: 1px solid #d8d8d8;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .07);
  padding: 2px 4px;
  margin-top: 3px;
}

.absence-reasons__absence__right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.icon__bg-circle {
  width: 30px;
  height: 30px;
  font-size: 1rem;
  cursor: pointer;
  border-radius: 50%;
  transition: background-color .3s;
  display: flex;
  align-items: center;
  justify-content: center;
  /* background-color: hsla(0,0%,100%,.16); */
}

.icon__bg-circle:hover, .icon__bg-circle:active {
  background-color: rgba(0, 0, 0, .16);
}

.dropdown.absent-handled__dropdown ul li, .dropdown.sub-status__dropdown ul li {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: normal;
}

.sub__user-header__right {
  display: flex;
}

.absence-status {
  border: 1px solid #d8d8d8;
  border-radius: 4px;
  padding: 5px 12px;
  margin-bottom: 5px;
}

.absence__status {
  position: relative;
  display: flex;
  justify-content: flex-end;
  margin-right: 19px;
  cursor: pointer;
}

.absence__status__dropdown {
  right: 24px;
}

label.d-label {
  font-size: 0.9rem;
  color: #728195;
}

.absence-reasons__absence__title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  margin-top: 0.5rem;
}

.text-link {
  color: var(--blue);
}

.sub-lesson__others__user--absent {
  color: rgba(250, 86, 90, 1);
  text-decoration: line-through;
  cursor: help;
}

.other-lesson-user--absent {
  color: rgba(250, 86, 90, 1);
  text-decoration: line-through;
  cursor: help;
}

.sub-status__dropdown--loading ul {
  opacity: 0;
  pointer-events: none;
}

.sub-status__dropdown .do-spinner__container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dot-yellow {
  background-color: #f0b100;
  border-color: #f0b100;
}

</style>



// WEBPACK FOOTER //
// SubsUser.vue?798aef08